import { useEffect, useState } from 'react'

import { HOME_DEEPLINK } from 'constants/deeplinks'
import { useNavigate } from 'react-router-dom'
import { useForm, Controller, Resolver } from 'react-hook-form'
import { normalizeLanguage, resolveDeviceLanguage } from 'utils'
import { CurrencyTrackName, LanguagesEnum, LanguageTrackName } from 'common/enums/languages'
import { useAppInfo } from 'hooks/useAppInfo'
import { useLeadMortgage } from 'hooks/useLeadMortgage'
import { routes } from 'routes'
import { useUserInfo } from 'hooks/useUserInfo'
import { FeedbackPage } from 'components/FeedbackPage'
import { FeedbackPageFlow, FeedbackPageType } from 'components/FeedbackPage/types'
import EqualHousing from 'components/Icons/EqualHousing'

import { zodResolver } from '@hookform/resolvers/zod'
import IcArrowleft from '@interco/icons/orange/MD/ic_arrowleft'
import IcChevronup from '@interco/icons/orange/SM/ic_chevronup'
import { InputText, Text, Button } from '@interco/inter-toranja'
import { Accordion } from '@interco/inter-ui/components/Accordion'
import { Button as ButtonUI } from '@interco/inter-ui/components/Button'
import { Radio } from '@interco/inter-ui/components/Radio'
import { interWbNavigate } from '@interco/inter-webview-bridge'
import { TrackData } from '@interco/track'

import { locales } from './data'
import { FormInputField } from './FormInputField'
import { buildLeadFormSchema, LeadFormData, LeadFormValues } from './schema'

const formatCurrencyDisplay = (raw: string) => {
  if (!raw) return ''
  const [integer, decimal] = raw.split('.')
  const formattedInteger = integer
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Number(integer))
    : ''
  return decimal !== undefined ? `${formattedInteger}.${decimal}` : formattedInteger
}

const sanitizeCurrencyInput = (value: string) => {
  const stripped = value.replace(/[^\d.]/g, '')
  const dot = stripped.indexOf('.')
  return dot === -1
    ? stripped
    : stripped.slice(0, dot + 1) +
        stripped
          .slice(dot + 1)
          .replace(/\D/g, '')
          .slice(0, 2)
}

export function LeadForm() {
  const [feedbackType, setFeedbackType] = useState<FeedbackPageType | null>(null)
  const [isRegulatoryInfoExpanded, setIsRegulatoryInfoExpanded] = useState(false)

  const navigate = useNavigate()
  const { appInfo } = useAppInfo()
  const { userInfo } = useUserInfo()
  const { mutateAsync: submitLead } = useLeadMortgage()

  const selectedLanguage = normalizeLanguage(resolveDeviceLanguage(appInfo), LanguagesEnum.PT_BR)
  const locale = locales[selectedLanguage]

  const schema = buildLeadFormSchema(locale.errors)

  const {
    control,
    handleSubmit,
    formState: { isSubmitted, isSubmitting, isValid },
  } = useForm<LeadFormValues, unknown, LeadFormData>({
    // The schema requires hasProperty to be a boolean, but the field starts unanswered
    // (undefined) until the user picks an option, so the resolver's inferred type is cast here.
    resolver: zodResolver(schema) as Resolver<LeadFormValues, unknown, LeadFormData>,
    mode: 'onTouched',
    values: {
      firstName: '',
      lastName: '',
      phone: userInfo?.phoneNumber ?? undefined,
      email: userInfo?.email ?? '',
      purchasePrice: '',
      contactMethod: '',
      hasProperty: undefined,
    },
  })

  const onSubmit = async (data: LeadFormData) => {
    try {
      await submitLead(data)

      const productName = 'mortgage'
      const flowPage = 'landing_page'
      const step = 'send_form_success'

      TrackData.log({
        product_name: productName,
        flow: flowPage,
        step,
        screen_name: `${productName}/${flowPage}/${step}`,
        property_value: data.purchasePrice,
        currency: CurrencyTrackName[selectedLanguage],
        scenario: LanguageTrackName[selectedLanguage],
      })

      setFeedbackType(FeedbackPageType.SUCCESS)
    } catch (error) {
      const productName = 'mortgage'
      const flowPage = 'landing_page'
      const step = 'send_form_error'

      TrackData.log({
        product_name: productName,
        flow: flowPage,
        step,
        screen_name: `${productName}/${flowPage}/${step}`,
        property_value: data.purchasePrice,
        currency: CurrencyTrackName[selectedLanguage],
        scenario: LanguageTrackName[selectedLanguage],
        error_message: error instanceof Error ? error.message : String(error),
      })

      setFeedbackType(FeedbackPageType.ERROR)
    }
  }

  const onGoBackHome = () => {
    interWbNavigate.openDeepLink(HOME_DEEPLINK)
  }

  useEffect(() => {
    const productName = 'mortgage'
    const flow = 'landing_page'
    const step = 'inform_details'

    TrackData.log({
      product_name: productName,
      flow,
      step,
      screen_name: `${productName}/${flow}/${step}`,
      scenario: LanguageTrackName[selectedLanguage],
    })
  }, [selectedLanguage])

  if (feedbackType) {
    return (
      <FeedbackPage
        feedbackType={feedbackType}
        flow={FeedbackPageFlow.LEAD_FORM}
        onBeforeButtonClick={onGoBackHome}
      />
    )
  }

  return (
    <>
      <div className="flex flex-col gap-6 pb-[100px]">
        <div className="flex flex-col items-start px-3 pt-2 gap-4">
          <ButtonUI type="button" variant="link" onClick={() => navigate(routes.lead)}>
            <IcArrowleft width={24} height={24} color="var(--color-icon-neutral-primary)" />
          </ButtonUI>

          <div className="px-3">
            <Text as="h1" textType="title" textSize="large">
              {locale.header.title}
            </Text>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="flex flex-col gap-4 px-6">
            <FormInputField
              name="firstName"
              control={control}
              isSubmitted={isSubmitted}
              label={locale.fields.firstName.label}
              placeholder={locale.fields.firstName.placeholder}
            />

            <FormInputField
              name="lastName"
              control={control}
              isSubmitted={isSubmitted}
              label={locale.fields.lastName.label}
              placeholder={locale.fields.lastName.placeholder}
            />

            <FormInputField
              name="phone"
              control={control}
              isSubmitted={isSubmitted}
              label={locale.fields.phone.label}
              placeholder={locale.fields.phone.placeholder}
              inputMode="tel"
              mask="phone"
            />

            <FormInputField
              name="email"
              control={control}
              isSubmitted={isSubmitted}
              label={locale.fields.email.label}
              placeholder={locale.fields.email.placeholder}
              inputMode="email"
            />

            <FormInputField
              name="purchasePrice"
              control={control}
              isSubmitted={isSubmitted}
              label={locale.fields.purchasePrice.label}
              placeholder={locale.fields.purchasePrice.placeholder}
              prefix="$"
              inputMode="decimal"
              formatValue={formatCurrencyDisplay}
              parseChange={sanitizeCurrencyInput}
            />

            <Controller
              name="contactMethod"
              control={control}
              render={({ field, fieldState }) => {
                const selectedOption = locale.fields.contactMethod.options.find(
                  (opt) => opt.value === field.value,
                )
                return (
                  <div className="relative w-full">
                    <InputText
                      label={locale.fields.contactMethod.label}
                      value={selectedOption?.label ?? ''}
                      state={fieldState.error && isSubmitted ? 'error' : 'enabled'}
                      error={
                        fieldState.error && isSubmitted
                          ? [fieldState.error.message ?? '']
                          : undefined
                      }
                    />
                    {/* Native select overlaid to trigger the OS picker (iOS/Android).
                        aria-hidden keeps screen readers on the Toranja Select above. */}
                    <select
                      aria-hidden
                      tabIndex={-1}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                      onBlur={field.onBlur}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    >
                      <option value="" disabled>
                        {locale.fields.contactMethod.label}
                      </option>
                      {locale.fields.contactMethod.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )
              }}
            />

            <Controller
              name="hasProperty"
              control={control}
              render={({ field, fieldState }) => (
                <div className="flex flex-col gap-2">
                  <Text as="p" textType="body" textSize="medium" colorVariant="secondary">
                    {locale.fields.hasProperty.label}
                  </Text>
                  <div className="flex flex-col gap-2">
                    {locale.fields.hasProperty.options.map((opt) => (
                      <Radio
                        key={opt.value}
                        id={`hasProperty-${opt.value}`}
                        name="hasProperty"
                        value={opt.value}
                        label={opt.label}
                        checked={field.value === (opt.value === 'true')}
                        onChange={() => field.onChange(opt.value === 'true')}
                      />
                    ))}
                  </div>
                  {fieldState.error && isSubmitted && (
                    <Text
                      as="p"
                      textType="body"
                      textSize="small"
                      colorScheme="feedback"
                      colorVariant="error"
                    >
                      {fieldState.error.message}
                    </Text>
                  )}
                </div>
              )}
            />
          </div>

          <div className="px-6 pt-4">
            <Accordion
              className="!px-0 !py-6 !border-b-0"
              labelComponent={
                <div className="flex w-full justify-between items-center gap-2">
                  <Text as="p" textType="body" textSize="medium" textWeight="bold">
                    {locale.regulatoryInfo.title}
                  </Text>

                  {isRegulatoryInfoExpanded ? (
                    <IcChevronup fontSize={16} color="var(--color-icon-neutral-primary)" />
                  ) : (
                    <IcChevronup
                      className="transform rotate-180"
                      fontSize={16}
                      color="var(--color-icon-neutral-primary)"
                    />
                  )}
                </div>
              }
              expanded={isRegulatoryInfoExpanded}
              onClick={() => setIsRegulatoryInfoExpanded((prev) => !prev)}
            >
              <div className="flex flex-col gap-2">
                {locale.regulatoryInfo.paragraphs.map((paragraph) => (
                  <Text
                    key={paragraph}
                    as="p"
                    textType="body"
                    textSize="medium"
                    colorVariant="secondary"
                  >
                    {paragraph}
                  </Text>
                ))}
              </div>
            </Accordion>
          </div>

          <EqualHousing className="mt-2 ml-6" />

          <div className="fixed bottom-0 left-0 right-0 bg-[var(--neutral-theme)] p-6 max-h-[initial] border-t border-[var(--color-border-neutral-default)]">
            <Button
              style={{ width: '100%' }}
              hierarchy="primary"
              label={locale.actionButton.title}
              onClick={handleSubmit(onSubmit)}
              loading={isSubmitting}
              disabled={!isValid && isSubmitted}
            />
          </div>
        </form>
      </div>
    </>
  )
}
